import React, { useState } from 'react';
// @ts-ignore
import styles from './EReceptListItemSuklPerMedicament.module.scss';
import { EReceptListLitemProps } from '../EReceptListItem';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useAppContext } from '../../../../Services/AppContext';
import { Theme } from '../../../../Enums/Theme.enum';
import LinkButton from '../../../Button/LinkButton';
import useTranslation from '../../../../Hooks/useTranslation';
import Text from '../../../Base/Text/Text';
import { useCreateUserMedicationLastedMutation } from '../../../../graphql/graphql';
import apiClient from '../../../../Services/ApiClient';
import useNotification from '../../../../Hooks/useNotification.hook';

interface Props extends EReceptListLitemProps {
	handleOpenDetail: () => void;
	handleDuplicate: (id: string) => void;
}

const EReceptListItemSuklPerMedicament: React.FC<Props> = ({
	item,
	handleOpenDetail,
	handleDuplicate,
	patientId,
}) => {
	const { dateFormatMoment, workplaceId } = useAppContext();
	const { t } = useTranslation();
	const { showNotificationSuccess } = useNotification();
	const [addToPermanentMedication] = useCreateUserMedicationLastedMutation();
	const [
		isAddToPermanentMedicationLoading,
		setIsAddToPermanentMedicationLoading,
	] = useState(false);

	const handleAddToPermanentMedication = async ({
		id,
		data,
	}: {
		id?: string;
		data?: { name: string };
	}) => {
		setIsAddToPermanentMedicationLoading(true);
		if (id) {
			const duplicationData = await apiClient.get(
				`/prescription/${item.id}/duplicate`,
			);

			const prescriptionMedicamentId: string | undefined =
				duplicationData.data?.formData?.default?.medicaments?.hvlp?.find(
					(item: any) => item?.item?.id === id,
				).id;

			if (prescriptionMedicamentId) {
				const result = await addToPermanentMedication({
					input: {
						userId: patientId,
						workplaceId,
						prescriptionId: item.id,
						prescriptionMedicamentId,
					},
				});

				if ('data' in result) {
					showNotificationSuccess({
						content: t(
							'component.eReceptListItemSuklPerMedicament.successfullyAdd',
						),
					});
				}
			}
		}

		if (data) {
			const result = await addToPermanentMedication({
				input: {
					userId: patientId,
					workplaceId,
					prescriptionId: item.id,
					prescriptionMedicamentData: { name: data.name, formType: 'iplp' },
				},
			});

			if ('data' in result) {
				showNotificationSuccess({
					content: t(
						'component.eReceptListItemSuklPerMedicament.successfullyAdd',
					),
				});
			}
		}

		setTimeout(() => {
			setIsAddToPermanentMedicationLoading(false);
		}, 1000);
	};

	const data = item.items;

	return (
		<div className={styles.wrapper}>
			{item.showDate && (
				<div className={styles.date}>
					{getMomentDate(item.createdAt).format(dateFormatMoment)}
				</div>
			)}
			{data &&
				data.map((i, index) => {
					const itemData = item?.data?.PLP?.find((item: any) => {
						return item?.HVLPReg?.Kod === i.prescriptionMedicament?.code;
					});

					let prescriptionMedicament: any = i.prescriptionMedicament;
					let quantity = i.quantity;
					const isIPLP = i.extra?.iplp ?? false;

					if (!prescriptionMedicament) {
						prescriptionMedicament = {
							id: i.id,
							name: i.extra?.iplp?.name,
							form: i.extra?.iplp?.form,
							routeOfAdministration: i.extra?.iplp?.routeOfAdministration,
							preparationProcedure: i.extra?.iplp?.instruction,
						};
					}

					const {
						name,
						addition,
						strength,
						form,
						packaging,
						atc,
						code,
						routeOfAdministration,
						preparationProcedure,
						id,
					} = prescriptionMedicament || {};

					return (
						<div className={styles.item} key={index}>
							<div className={styles.content}>
								<div className={styles.medicament}>
									<div className={styles.title}>
										{name} <span>{addition}</span>
									</div>
									{!!item.validUntil && (
										<div>
											<Text
												tx={'component.ePrescription.expirationDate'}
												firstUpper={true}
												inline={true}
											/>{' '}
											{getMomentDate(item.validUntil).format(dateFormatMoment)}
										</div>
									)}
								</div>
								<div className={styles.moreInfo}>
									{strength && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.strength'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{strength}</div>
										</div>
									)}
									{form && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.form'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{form}</div>
										</div>
									)}
									{packaging && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.packaging'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{packaging}</div>
										</div>
									)}
									{atc && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.atc'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{atc}</div>
										</div>
									)}
									{code && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.code'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{code}</div>
										</div>
									)}
									{routeOfAdministration && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.howToAdministrate'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{routeOfAdministration}</div>
										</div>
									)}
									{quantity && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.amount'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{quantity}</div>
										</div>
									)}
									{itemData?.Uhrada && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.payment'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{itemData.Uhrada}</div>
										</div>
									)}
									{itemData?.Navod && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.dosage'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{itemData.Navod}</div>
										</div>
									)}
									{preparationProcedure && (
										<div className={styles.moreInfoItem}>
											<Text
												tx='component.ePrescription.preparationProcedure'
												firstUpper
												className={styles.title}
											/>
											<div className={styles.text}>{preparationProcedure}</div>
										</div>
									)}
								</div>
							</div>
							<div className={styles.controls}>
								<LinkButton
									onClick={handleOpenDetail}
									title={t('component.ePrescription.detail') ?? ''}
									theme={Theme.BLUE}
								/>
								{id && (
									<LinkButton
										onClick={() => handleDuplicate(id)}
										title={t('component.ePrescription.again') ?? ''}
										theme={Theme.BLUE}
									/>
								)}
								{id && !isIPLP && (
									<LinkButton
										onClick={() => handleAddToPermanentMedication({ id })}
										title='component.ePrescription.addToPermanentMedication'
										theme={Theme.BLUE}
										isLoading={isAddToPermanentMedicationLoading}
									/>
								)}
								{isIPLP && (
									<LinkButton
										onClick={() =>
											handleAddToPermanentMedication({ data: i.extra?.iplp })
										}
										title='component.ePrescription.addToPermanentMedication'
										theme={Theme.BLUE}
										isLoading={isAddToPermanentMedicationLoading}
									/>
								)}
							</div>
						</div>
					);
				})}
		</div>
	);
};

export default EReceptListItemSuklPerMedicament;
